import { api } from './client';

export type CheckinState = 'NOT_STARTED' | 'STARTED' | 'IN_PROGRESS' | 'FINISHED' | 'ABANDONED';
export type MilestoneLevel = 'MONTH' | 'WEEK' | 'DAY';
export type MilestoneStatus = 'OPEN' | 'DONE' | 'DROPPED' | 'CARRIED';

export interface KindStatus {
  kind: 'daily' | 'weekly' | 'monthly';
  lastAt: string | null;
  daysSince: number | null;
  doneToday: boolean;
  periodKey: string;
  state: CheckinState;
  openSessionId: string | null;
  lastFinishedAt: string | null;
}

export interface Checkin {
  id: string;
  kind: string;
  performedAt: string;
  note: string | null;
  state: CheckinState;
  periodKey: string | null;
  startedAt: string | null;
  lastStepAt: string | null;
  finishedAt: string | null;
  summary: string | null;
  /** FINISHED by the server sweep (steps but no finish call), not by finish(). */
  autoClosed: boolean;
  /** When the one-time "come back and finish" nudge went out. */
  remindedAt: string | null;
}

export interface CheckinStep {
  id: string;
  at: string;
  kind: string;
  body: string;
}

export interface Milestone {
  id: string;
  level: MilestoneLevel;
  periodKey: string;
  title: string;
  detail: string | null;
  status: MilestoneStatus;
  parentId: string | null;
  createdCheckinId: string | null;
  closedCheckinId: string | null;
  carriedToId: string | null;
  taskId: string | null;
  periodStart: string;
  periodEnd: string;
}

export function getCheckinStatus(): Promise<KindStatus[]> {
  return api<KindStatus[]>('/checkins/status');
}

export function getCheckins(limit = 200): Promise<Checkin[]> {
  return api<Checkin[]>('/checkins', { query: { limit } });
}

/** Every session of one ritual in one period (a period can hold more than one). */
export function getCheckinsForPeriod(kind: string, periodKey: string): Promise<Checkin[]> {
  return api<Checkin[]>('/checkins', { query: { kind, periodKey } });
}

export function getCheckinSteps(sessionId: string): Promise<CheckinStep[]> {
  return api<CheckinStep[]>(`/checkins/${sessionId}/steps`);
}

export function startCheckin(kind: string): Promise<{ sessionId: string }> {
  return api<{ sessionId: string }>('/checkins/start', { method: 'POST', body: { kind } });
}

export function finishCheckin(sessionId: string, summary: string): Promise<Checkin> {
  return api<Checkin>(`/checkins/${sessionId}/finish`, { method: 'POST', body: { summary } });
}

/** Corrects a summary already on record (any state), without re-finishing. */
export function updateCheckinSummary(sessionId: string, summary: string): Promise<Checkin> {
  return api<Checkin>(`/checkins/${sessionId}`, { method: 'PATCH', body: { summary } });
}

export function getMilestones(
  level?: MilestoneLevel,
  openOnly = false,
  periodKey?: string
): Promise<Milestone[]> {
  return api<Milestone[]>('/checkins/milestones', {
    query: { level, openOnly: openOnly ? 'true' : undefined, periodKey }
  });
}

export function createMilestone(body: {
  level: MilestoneLevel;
  title: string;
  parentId?: string | null;
}): Promise<Milestone> {
  return api<Milestone>('/checkins/milestones', { method: 'POST', body });
}

export function closeMilestone(id: string, status: 'DONE' | 'DROPPED'): Promise<Milestone> {
  return api<Milestone>(`/checkins/milestones/${id}/close`, { method: 'POST', body: { status } });
}

export function carryMilestone(id: string): Promise<Milestone> {
  return api<Milestone>(`/checkins/milestones/${id}/carry`, { method: 'POST', body: {} });
}
